import React, { useState, useEffect } from "react";
import { Link, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import DoctorAppointments from "./DoctorAppointments";
import DoctorPatients from "./DoctorPatients";
import DoctorNotifications from "./DoctorNotifications";
import DoctorLocations from "./DoctorLocations";
import DoctorSelfProfile from "./DoctorSelfProfile";
import FloatingParticles from "../components/FloatingParticles";



export default function DoctorLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [doctorInfo, setDoctorInfo] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        const updateDoc = () => {
            const doc = localStorage.getItem("doctor");
            if (doc) setDoctorInfo(JSON.parse(doc));
        };
        updateDoc();
        window.addEventListener("storage", updateDoc);
        return () => window.removeEventListener("storage", updateDoc);
    }, []);

    const logout = () => {
        localStorage.removeItem("doctor");
        navigate("/login");
    };

    const navItems = [
        { path: "/doctor-portal/appointments", icon: "📅", label: "Dashboard" },
        { path: "/doctor-portal/patients", icon: "👥", label: "My Patients" },
        { path: "/doctor-portal/notifications", icon: "🔔", label: "Notifications" },
        { path: "/doctor-portal/locations", icon: "🏥", label: "Location Schedules" },
        { url: "/Screening_test/app", icon: "🩺", label: "Screening Tests", external: true },
        { path: "/doctor-portal/profile", icon: "👤", label: "My Profile" },
    ];

    const closeSidebar = () => setSidebarOpen(false);

    if (!doctorInfo) return null;

    return (
        <div style={{ display: "flex", minHeight: "100vh", background: "#f8fafc", color: "#1e293b", fontFamily: "sans-serif", position: "relative", overflow: "hidden" }}>
            <style>{`
                @keyframes float {
                    0% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-20px) rotate(10deg); }
                    100% { transform: translateY(0px) rotate(0deg); }
                }
                .animate-float { animation: float infinite ease-in-out; }

                /* ── Sidebar ── */
                .doc-sidebar {
                    width: 256px;
                    min-width: 256px;
                    height: 100vh;
                    position: fixed;
                    top: 0;
                    left: 0;
                    z-index: 200;
                    display: flex;
                    flex-direction: column;
                    background: rgba(255,255,255,0.85);
                    backdrop-filter: blur(14px);
                    border-right: 1px solid rgba(224,242,254,0.8);
                    box-shadow: 0 8px 32px rgba(14,165,233,0.08);
                    transition: transform 0.32s cubic-bezier(0.4,0,0.2,1);
                }

                /* ── Overlay (mobile only) ── */
                .doc-overlay {
                    display: none;
                    position: fixed;
                    inset: 0;
                    z-index: 150;
                    background: rgba(15,23,42,0.45);
                    backdrop-filter: blur(2px);
                    animation: fadeIn 0.2s ease;
                }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

                /* ── Main content ── */
                .doc-main {
                    flex: 1;
                    margin-left: 256px;
                    padding: 32px;
                    overflow-y: auto;
                    position: relative;
                    z-index: 0;
                    min-height: 100vh;
                }

                /* ── Mobile top bar ── */
                .doc-topbar {
                    display: none;
                    align-items: center;
                    gap: 12px;
                    position: sticky;
                    top: 0;
                    z-index: 100;
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(12px);
                    border-bottom: 1px solid #e0f2fe;
                    padding: 12px 16px;
                    margin: -16px -16px 16px -16px;
                }

                .doc-hamburger {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 6px;
                    border-radius: 8px;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    transition: background 0.2s;
                }
                .doc-hamburger:hover { background: #f0f9ff; }
                .doc-hamburger span {
                    display: block;
                    width: 22px;
                    height: 2.5px;
                    background: #0284c7;
                    border-radius: 2px;
                    transition: all 0.3s ease;
                }
                /* Animate to X when open */
                .doc-hamburger.open span:nth-child(1) {
                    transform: translateY(7.5px) rotate(45deg);
                }
                .doc-hamburger.open span:nth-child(2) {
                    opacity: 0;
                    transform: scaleX(0);
                }
                .doc-hamburger.open span:nth-child(3) {
                    transform: translateY(-7.5px) rotate(-45deg);
                }

                /* ── Responsive breakpoint ── */
                @media (max-width: 768px) {
                    .doc-sidebar {
                        transform: translateX(-100%);
                    }
                    .doc-sidebar.open {
                        transform: translateX(0);
                    }
                    .doc-overlay {
                        display: block;
                    }
                    .doc-main {
                        margin-left: 0;
                        padding: 16px;
                    }
                    .doc-topbar {
                        display: flex;
                    }
                }
            `}</style>

            <FloatingParticles />

            {/* Mobile overlay */}
            {sidebarOpen && (
                <div className="doc-overlay" onClick={closeSidebar} />
            )}

            {/* Sidebar */}
            <div className={`doc-sidebar ${sidebarOpen ? "open" : ""}`}>
                <div style={{ padding: "24px" }}>
                    <div style={{ color: "#0284c7", fontWeight: 700, fontSize: "22px", display: "flex", alignItems: "center", gap: "8px", marginBottom: "36px" }}>
                        <span style={{ fontSize: "28px" }}>🏥</span> MediBook
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {navItems.map((item) => (
                            <Link
                                key={item.path || item.url}
                                to={item.path || "#"}
                                onClick={(e) => {
                                    if (item.external) {
                                        e.preventDefault();
                                        window.location.href = item.url;
                                    }
                                    closeSidebar();
                                }}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "12px 16px",
                                    borderRadius: "12px",
                                    textDecoration: "none",
                                    fontWeight: 500,
                                    fontSize: "15px",
                                    transition: "all 0.25s",
                                    background: location.pathname === item.path
                                        ? "linear-gradient(to right, #0ea5e9, #0284c7)"
                                        : "transparent",
                                    color: location.pathname === item.path ? "#fff" : "#64748b",
                                    boxShadow: location.pathname === item.path ? "0 4px 12px rgba(14,165,233,0.3)" : "none",
                                }}
                            >
                                <span style={{ fontSize: "18px" }}>{item.icon}</span>
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </div>
                </div>

                <div style={{ marginTop: "auto", padding: "24px", borderTop: "1px solid rgba(224,242,254,0.6)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", background: "rgba(255,255,255,0.6)", padding: "12px", borderRadius: "12px", border: "1px solid #fff" }}>
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#0ea5e9,#0284c7)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "14px", flexShrink: 0 }}>
                            {doctorInfo.name.charAt(0)}
                        </div>
                        <div style={{ overflow: "hidden" }}>
                            <div style={{ fontWeight: 600, fontSize: "14px", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Dr. {doctorInfo.name}</div>
                            <div style={{ fontSize: "12px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{doctorInfo.specialization}</div>
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        style={{ width: "100%", padding: "10px 0", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", background: "#f1f5f9", color: "#475569", borderRadius: "12px", border: "none", cursor: "pointer", fontWeight: 500, fontSize: "14px", transition: "all 0.2s" }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#ef4444"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#475569"; }}
                    >
                        <span>🚪</span> Logout
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="doc-main">
                {/* Mobile top bar */}
                <div className="doc-topbar">
                    <button
                        id="doc-hamburger-btn"
                        className={`doc-hamburger ${sidebarOpen ? "open" : ""}`}
                        onClick={() => setSidebarOpen(prev => !prev)}
                        aria-label="Toggle navigation menu"
                    >
                        <span />
                        <span />
                        <span />
                    </button>
                    <div style={{ color: "#0284c7", fontWeight: 700, fontSize: "18px", display: "flex", alignItems: "center", gap: "6px" }}>
                        <span>🏥</span> MediBook
                    </div>
                </div>

                <Routes>
                    <Route path="/" element={<Navigate to="appointments" replace />} />
                    <Route path="appointments" element={<DoctorAppointments doctor={doctorInfo} />} />
                    <Route path="patients" element={<DoctorPatients doctor={doctorInfo} />} />
                    <Route path="notifications" element={<DoctorNotifications doctor={doctorInfo} />} />
                    <Route path="locations" element={<DoctorLocations doctor={doctorInfo} />} />
                    <Route path="profile" element={<DoctorSelfProfile doctor={doctorInfo} />} />
                </Routes>
            </div>
        </div>
    );
}
